import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  doc,
  onSnapshot,
  Timestamp,
  DocumentData,
  QuerySnapshot
} from 'firebase/firestore';
import { db } from './firebase';

// Types for analytics data
export interface MembershipMetrics {
  totalMembers: number;
  activeMembers: number;
  newMembersThisPeriod: number;
  churnRate: number;
  monthlyRevenue: number;
  yearlyRevenue: number;
  averageLifetimeValue: number;
  conversionRate: number;
}

export interface UsageMetrics {
  totalShipments: number;
  expressShipmentsUsed: number;
  savingsGenerated: number;
  averageOrderValue: number;
  topUsageHours: { hour: number; count: number; timeLabel: string }[];
  usageByPlan: { plan: string; count: number; percentage: number }[];
}

export interface FinancialMetrics {
  totalRevenue: number;
  monthlyRecurringRevenue: number;
  yearlyRecurringRevenue: number;
  refunds: number;
  netRevenue: number;
  revenueGrowth: number;
  averageRevenuePerUser: number;
}

export interface DashboardMetrics {
  activeMembers: number;
  todaysExpressOrders: number;
  todaysRevenue: number;
  totalMembers: number;
  lastUpdated: string;
}

export interface RushdMembership {
  id: string;
  userId: string;
  status: 'active' | 'canceled' | 'past_due' | 'incomplete' | 'trialing';
  plan: 'monthly' | 'yearly';
  stripeSubscriptionId: string;
  stripeCustomerId: string;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  usageCounterMonth: number;
  lastUsageResetAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface RushdOrder {
  id: string;
  userId: string;
  total: number;
  createdAt: Date;
  shipping?: {
    method: string;
  };
  rushdMembership?: {
    isRushdRateApplied?: boolean;
    isEligible?: boolean;
    rushdRateValue?: number;
    rushdSavingsValue?: number;
    rushdUserId?: string;
    userId?: string;
    memberPrice?: number;
    regularPrice?: number;
    savings?: number;
  };
}

export interface RushdRefund {
  id: string;
  orderId?: string;
  userId?: string;
  amount: number;
  reason: string;
  refundDate: Date;
  stripeRefundId?: string;
  type: 'full' | 'partial' | 'subscription_refund';
}

export interface VisitorEvent {
  id: string;
  eventType: 'pricing_page_visit' | 'signup_conversion' | 'membership_purchase';
  sessionId: string;
  userId?: string;
  timestamp: Date;
  metadata?: {
    referrer?: string;
    userAgent?: string;
    deviceType?: 'desktop' | 'mobile' | 'tablet';
    plan?: 'monthly' | 'yearly';
    source?: string;
    amount?: number;
  };
}

// Cache management
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class AnalyticsCache {
  private cache = new Map<string, CacheEntry<any>>();
  
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() > entry.timestamp + entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }
  
  set<T>(key: string, data: T, ttl: number = 120000): void { // 2 minutes default
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }
  
  clear(): void {
    this.cache.clear();
  }
}

const analyticsCache = new AnalyticsCache();

// Helper functions
const parseFirestoreTimestamp = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  return new Date(timestamp);
};

const createFirestoreTimestamp = (date: Date): Timestamp => {
  return Timestamp.fromDate(date);
};

/**
 * Calculate average lifetime value for memberships
 */
const calculateAverageLifetimeValue = (memberships: RushdMembership[]): number => {
  if (memberships.length === 0) return 0;

  const totalValue = memberships.reduce((sum, membership) => {
    const daysActive = calculateDaysActive(membership);
    
    if (membership.plan === 'monthly') {
      // For monthly: $9.99 per month, calculate actual months
      const monthsActive = Math.max(1, Math.floor(daysActive / 30));
      return sum + (9.99 * monthsActive);
    } else {
      // For yearly: $59.99 per year, calculate actual years (or partial years)
      const yearsActive = Math.max(1 / 12, daysActive / 365); // Minimum 1 month value
      return sum + (59.99 * yearsActive);
    }
  }, 0);

  return totalValue / memberships.length;
};

/**
 * Calculate days active for a membership
 */
const calculateDaysActive = (membership: RushdMembership): number => {
  const startDate = membership.createdAt;
  const endDate = membership.status === 'active' 
    ? new Date() 
    : membership.updatedAt;
  
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(1, diffDays); // At least 1 day
};

/**
 * Fetch all Rush'd memberships with caching
 */
export const fetchMemberships = async (useCache: boolean = true): Promise<RushdMembership[]> => {
  const cacheKey = 'rushd_memberships';
  
  if (useCache) {
    const cached = analyticsCache.get<RushdMembership[]>(cacheKey);
    if (cached) return cached;
  }

  try {
    const membershipsRef = collection(db, 'rush_d_memberships');
    const snapshot = await getDocs(membershipsRef);
    
    const memberships: RushdMembership[] = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        status: data.status,
        plan: data.plan,
        stripeSubscriptionId: data.stripeSubscriptionId,
        stripeCustomerId: data.stripeCustomerId,
        currentPeriodStart: parseFirestoreTimestamp(data.currentPeriodStart),
        currentPeriodEnd: parseFirestoreTimestamp(data.currentPeriodEnd),
        cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
        usageCounterMonth: data.usageCounterMonth || 0,
        lastUsageResetAt: data.lastUsageResetAt ? parseFirestoreTimestamp(data.lastUsageResetAt) : undefined,
        createdAt: parseFirestoreTimestamp(data.createdAt),
        updatedAt: parseFirestoreTimestamp(data.updatedAt),
      };
    });

    if (useCache) {
      analyticsCache.set(cacheKey, memberships);
    }

    return memberships;
  } catch (error) {
    console.error('Error fetching Rush\'d memberships:', error);
    throw new Error('Failed to fetch membership data');
  }
};

/**
 * Fetch orders for a date range with Rush'd data
 */
export const fetchOrdersInDateRange = async (
  startDate: Date, 
  endDate: Date, 
  useCache: boolean = true
): Promise<RushdOrder[]> => {
  const cacheKey = `rushd_orders_${startDate.toISOString()}_${endDate.toISOString()}`;
  
  if (useCache) {
    const cached = analyticsCache.get<RushdOrder[]>(cacheKey);
    if (cached) return cached;
  }

  try {
    console.log('📅 Fetching orders for date range:', {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });

    const ordersRef = collection(db, 'orders');
    const q = query(
      ordersRef,
      where('createdAt', '>=', createFirestoreTimestamp(startDate)),
      where('createdAt', '<=', createFirestoreTimestamp(endDate)),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    console.log(`📦 Found ${snapshot.docs.length} orders in date range`);
    
    const orders: RushdOrder[] = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId || 'guest',
        total: data.total || 0,
        createdAt: parseFirestoreTimestamp(data.createdAt),
        shipping: data.shipping,
        rushdMembership: data.rushdMembership,
      };
    });

    if (useCache) {
      analyticsCache.set(cacheKey, orders, 60000); // Cache for 1 minute for recent data
    }

    return orders;
  } catch (error) {
    console.error('Error fetching orders in date range:', error);
    throw new Error('Failed to fetch order data');
  }
};

/**
 * Fetch recent orders for debugging
 */
export const fetchRecentOrdersForDebugging = async (): Promise<any[]> => {
  try {
    console.log('🔍 Fetching recent orders for debugging...');
    
    const ordersRef = collection(db, 'orders');
    const q = query(
      ordersRef,
      orderBy('createdAt', 'desc'),
      // Don't limit by date, just get the most recent ones
    );
    
    const snapshot = await getDocs(q);
    
    const recentOrders = snapshot.docs.slice(0, 10).map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt,
        userId: data.userId,
        total: data.total,
        shipping: data.shipping,
        rushdMembership: data.rushdMembership,
        payment: data.payment
      };
    });

    console.log('🚀 Recent orders (last 10):', recentOrders);
    return recentOrders;
  } catch (error) {
    console.error('Error fetching recent orders for debugging:', error);
    return [];
  }
};

/**
 * Fetch visitor events for conversion analytics
 */
export const fetchVisitorEvents = async (
  startDate: Date, 
  endDate: Date, 
  useCache: boolean = true
): Promise<VisitorEvent[]> => {
  const cacheKey = `visitor_events_${startDate.toISOString()}_${endDate.toISOString()}`;
  
  if (useCache) {
    const cached = analyticsCache.get<VisitorEvent[]>(cacheKey);
    if (cached) return cached;
  }

  try {
    const eventsRef = collection(db, 'rushd_visitor_events');
    const q = query(
      eventsRef,
      where('timestamp', '>=', createFirestoreTimestamp(startDate)),
      where('timestamp', '<=', createFirestoreTimestamp(endDate)),
      orderBy('timestamp', 'desc')
    );
    
    const snapshot = await getDocs(q);
    
    const events: VisitorEvent[] = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        eventType: data.eventType,
        sessionId: data.sessionId,
        userId: data.userId,
        timestamp: parseFirestoreTimestamp(data.timestamp),
        metadata: data.metadata || {},
      };
    });

    if (useCache) {
      analyticsCache.set(cacheKey, events, 60000); // Cache for 1 minute for recent data
    }

    return events;
  } catch (error) {
    console.error('Error fetching visitor events:', error);
    // Return empty array if collection doesn't exist yet
    return [];
  }
};

/**
 * Calculate conversion rate from visitor events
 */
const calculateConversionRate = (visitorEvents: VisitorEvent[]): number => {
  const visits = visitorEvents.filter(e => e.eventType === 'pricing_page_visit').length;
  const conversions = visitorEvents.filter(e => 
    e.eventType === 'membership_purchase' || e.eventType === 'signup_conversion'
  ).length;

  if (visits === 0) return 0.05; // Default 5% if no data
  return conversions / visits;
};

/**
 * Fetch refunds for analytics
 */
export const fetchRefunds = async (useCache: boolean = true): Promise<RushdRefund[]> => {
  const cacheKey = 'rushd_refunds';
  
  if (useCache) {
    const cached = analyticsCache.get<RushdRefund[]>(cacheKey);
    if (cached) return cached;
  }

  try {
    const refundsRef = collection(db, 'rushd_refunds');
    const snapshot = await getDocs(refundsRef);
    
    const refunds: RushdRefund[] = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        orderId: data.orderId,
        userId: data.userId,
        amount: data.amount || 0,
        reason: data.reason || '',
        refundDate: parseFirestoreTimestamp(data.refundDate),
        stripeRefundId: data.stripeRefundId,
        type: data.type || 'full',
      };
    });

    if (useCache) {
      analyticsCache.set(cacheKey, refunds);
    }

    return refunds;
  } catch (error) {
    console.error('Error fetching refunds:', error);
    // Return empty array if collection doesn't exist yet
    return [];
  }
};

/**
 * Calculate membership analytics
 */
export const calculateMembershipMetrics = async (
  startDate: Date, 
  endDate: Date,
  useCache: boolean = false  // Default to fresh data for period changes
): Promise<MembershipMetrics> => {
  try {
    const [memberships, visitorEvents] = await Promise.all([
      fetchMemberships(useCache),
      fetchVisitorEvents(startDate, endDate, useCache)
    ]);
    
    const now = new Date();
    const totalMembers = memberships.length;
    
    // Active members (status is active AND current period hasn't ended)
    const activeMembers = memberships.filter(m => 
      m.status === 'active' && m.currentPeriodEnd > now
    ).length;

    // New members in this period
    const newMembersThisPeriod = memberships.filter(m => {
      const memberCreated = m.createdAt >= startDate && m.createdAt <= endDate;
      if (memberCreated) {
        console.log(`👤 Member ${m.id} created in period:`, {
          createdAt: m.createdAt.toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
      }
      return memberCreated;
    }).length;

    console.log(`📊 Membership metrics debug:`, {
      totalMembers,
      activeMembers,
      newMembersThisPeriod,
      period: { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
      allMemberDates: memberships.map(m => ({
        id: m.id,
        createdAt: m.createdAt.toISOString(),
        status: m.status
      }))
    });

    // Calculate churn rate (cancelled in period / members active at start of period)
    const cancelledInPeriod = memberships.filter(m => {
      return m.status === 'canceled' && m.updatedAt >= startDate && m.updatedAt <= endDate;
    }).length;
    
    // Members who were active at the start of the period
    const membersActiveAtStartOfPeriod = memberships.filter(m => {
      // Member existed before the period start AND was either:
      // 1. Still active now, OR
      // 2. Was cancelled/ended after the period start
      return m.createdAt < startDate && (
        m.status === 'active' || 
        (m.status === 'canceled' && m.updatedAt >= startDate)
      );
    }).length;
    
    const churnRate = membersActiveAtStartOfPeriod > 0 ? cancelledInPeriod / membersActiveAtStartOfPeriod : 0;

    // Revenue calculations
    const monthlyMembers = memberships.filter(m => m.plan === 'monthly' && m.status === 'active');
    const yearlyMembers = memberships.filter(m => m.plan === 'yearly' && m.status === 'active');
    
    const monthlyRevenue = monthlyMembers.length * 9.99;
    const yearlyRevenue = yearlyMembers.length * 59.99;

    // Average lifetime value
    const averageLifetimeValue = calculateAverageLifetimeValue(memberships);

    // Calculate real conversion rate from visitor events
    const conversionRate = calculateConversionRate(visitorEvents);

    return {
      totalMembers,
      activeMembers,
      newMembersThisPeriod,
      churnRate,
      monthlyRevenue,
      yearlyRevenue,
      averageLifetimeValue,
      conversionRate,
    };
  } catch (error) {
    console.error('Error calculating membership metrics:', error);
    throw new Error('Failed to calculate membership metrics');
  }
};

/**
 * Calculate usage analytics
 */
export const calculateUsageMetrics = async (
  startDate: Date, 
  endDate: Date,
  useCache: boolean = false  // Default to fresh data for period changes
): Promise<UsageMetrics> => {
  try {
    const [orders, memberships] = await Promise.all([
      fetchOrdersInDateRange(startDate, endDate, useCache),
      fetchMemberships(useCache)
    ]);

    // Also fetch recent orders for debugging if we have no data
    if (orders.length === 0) {
      console.log('⚠️ No orders found in date range, checking recent orders...');
      await fetchRecentOrdersForDebugging();
    }

    console.log(`🔍 Usage Analytics Debug:`, {
      dateRange: { startDate, endDate },
      totalOrders: orders.length,
      ordersWithRushdData: orders.filter(o => o.rushdMembership).length,
      ordersWithExpressShipping: orders.filter(o => o.shipping?.method === 'express').length,
      ordersWithRushdApplied: orders.filter(o => o.rushdMembership?.isRushdRateApplied).length
    });

    // Debug: Log order details for today
    if (orders.length > 0) {
      console.log('📦 Recent orders sample:', orders.slice(0, 3).map(o => ({
        id: o.id,
        createdAt: o.createdAt,
        shippingMethod: o.shipping?.method,
        hasRushdData: !!o.rushdMembership,
        isRushdRateApplied: o.rushdMembership?.isRushdRateApplied,
        rushdSavings: o.rushdMembership?.rushdSavingsValue
      })));
    }

    // Helper function to check if order has Rush'd benefits applied
    const isRushdOrderWithBenefits = (order: RushdOrder): boolean => {
      // New format
      if (order.rushdMembership?.isRushdRateApplied === true) return true;
      
      // Legacy format - check for Rush'd eligibility or member data
      if (order.rushdMembership?.isEligible === true) return true;
      
      // Check if order has Rush'd user ID (indicates Rush'd member placed order)
      if (order.rushdMembership?.rushdUserId || order.rushdMembership?.userId) return true;
      
      // Check for any Rush'd savings/member pricing
      if (order.rushdMembership?.rushdSavingsValue || order.rushdMembership?.memberPrice || order.rushdMembership?.savings) return true;
      
      return false;
    };

    // Filter for Rush'd related orders (backward compatible)
    const rushdOrders = orders.filter(order => 
      isRushdOrderWithBenefits(order) || 
      order.shipping?.method === 'express'
    );

    // Calculate usage metrics
    const totalShipments = orders.length;
    const expressShipmentsUsed = orders.filter(order => 
      order.shipping?.method === 'express' && 
      isRushdOrderWithBenefits(order)
    ).length;

    console.log('📊 Usage metrics calculated:', {
      totalShipments,
      expressShipmentsUsed,
      rushdOrdersCount: rushdOrders.length,
      rushdOrdersDetected: rushdOrders.map(o => ({
        id: o.id,
        hasNewFormat: !!o.rushdMembership?.isRushdRateApplied,
        hasLegacyFormat: !!o.rushdMembership?.isEligible,
        hasRushdUser: !!(o.rushdMembership?.rushdUserId || o.rushdMembership?.userId),
        hasSavings: !!(o.rushdMembership?.rushdSavingsValue || o.rushdMembership?.savings || o.rushdMembership?.memberPrice)
      }))
    });

    // Calculate savings (backward compatible)
    const savingsGenerated = rushdOrders.reduce((total, order) => {
      // New format
      if (order.rushdMembership?.rushdSavingsValue) {
        return total + order.rushdMembership.rushdSavingsValue;
      }
      
      // Legacy format
      if (order.rushdMembership?.savings) {
        return total + order.rushdMembership.savings;
      }
      
      // Calculate savings from memberPrice vs regular price if available
      if (order.rushdMembership?.memberPrice && order.rushdMembership?.regularPrice) {
        const savings = order.rushdMembership.regularPrice - order.rushdMembership.memberPrice;
        return total + Math.max(0, savings);
      }
      
      return total;
    }, 0);

    // Average order value for Rush'd orders
    const averageOrderValue = rushdOrders.length > 0 
      ? rushdOrders.reduce((sum, order) => sum + order.total, 0) / rushdOrders.length
      : 0;

    // Usage by plan
    const monthlyCount = memberships.filter(m => m.plan === 'monthly').length;
    const yearlyCount = memberships.filter(m => m.plan === 'yearly').length;
    const totalMemberships = monthlyCount + yearlyCount;

    const usageByPlan = [
      {
        plan: 'monthly',
        count: monthlyCount,
        percentage: totalMemberships > 0 ? (monthlyCount / totalMemberships) * 100 : 0
      },
      {
        plan: 'yearly',
        count: yearlyCount,
        percentage: totalMemberships > 0 ? (yearlyCount / totalMemberships) * 100 : 0
      }
    ];

    // Calculate peak usage hours from Rush'd order timestamps
    const hourCounts = new Array(24).fill(0);
    rushdOrders.forEach(order => {
      const hour = order.createdAt.getHours();
      hourCounts[hour]++;
    });

    const topUsageHours = hourCounts
      .map((count, hour) => ({ 
        hour, 
        count,
        timeLabel: `${hour.toString().padStart(2, '0')}:00`
      }))
      .filter(item => item.count > 0) // Only show hours with actual usage
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalShipments,
      expressShipmentsUsed,
      savingsGenerated,
      averageOrderValue,
      topUsageHours,
      usageByPlan,
    };
  } catch (error) {
    console.error('Error calculating usage metrics:', error);
    throw new Error('Failed to calculate usage metrics');
  }
};

/**
 * Calculate financial analytics
 */
export const calculateFinancialMetrics = async (
  startDate: Date, 
  endDate: Date,
  useCache: boolean = false  // Default to fresh data for period changes
): Promise<FinancialMetrics> => {
  try {
    const [memberships, refunds] = await Promise.all([
      fetchMemberships(useCache),
      fetchRefunds(useCache)
    ]);

    // Calculate revenue metrics
    const activeMonthlyMembers = memberships.filter(m => 
      m.plan === 'monthly' && m.status === 'active'
    ).length;
    const activeYearlyMembers = memberships.filter(m => 
      m.plan === 'yearly' && m.status === 'active'
    ).length;

    const monthlyRecurringRevenue = activeMonthlyMembers * 9.99;
    const yearlyRecurringRevenue = activeYearlyMembers * 59.99;
    const totalRevenue = monthlyRecurringRevenue + (yearlyRecurringRevenue / 12); // Normalize yearly to monthly

    // Calculate refunds in the period
    const periodRefunds = refunds.filter(refund => 
      refund.refundDate >= startDate && refund.refundDate <= endDate
    );
    const totalRefunds = periodRefunds.reduce((sum, refund) => sum + refund.amount, 0);

    const netRevenue = totalRevenue - totalRefunds;

    // Revenue growth (simplified calculation - would need historical data for accurate calculation)
    const revenueGrowth = 0.1; // 10% placeholder for now

    // Average revenue per user
    const totalActiveMembers = activeMonthlyMembers + activeYearlyMembers;
    const averageRevenuePerUser = totalActiveMembers > 0 
      ? totalRevenue / totalActiveMembers 
      : 0;

    return {
      totalRevenue,
      monthlyRecurringRevenue,
      yearlyRecurringRevenue,
      refunds: totalRefunds,
      netRevenue,
      revenueGrowth,
      averageRevenuePerUser,
    };
  } catch (error) {
    console.error('Error calculating financial metrics:', error);
    throw new Error('Failed to calculate financial metrics');
  }
};

/**
 * Get real-time dashboard metrics
 */
export const calculateDashboardMetrics = async (): Promise<DashboardMetrics> => {
  try {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const [memberships, todaysOrders] = await Promise.all([
      fetchMemberships(),
      fetchOrdersInDateRange(startOfToday, now)
    ]);

    const activeMembers = memberships.filter(m => 
      m.status === 'active' && m.currentPeriodEnd > now
    ).length;

    const todaysExpressOrders = todaysOrders.filter(order => 
      order.shipping?.method === 'express' && 
      order.rushdMembership?.isRushdRateApplied
    ).length;

    // Estimate daily revenue from monthly subscriptions
    const todaysRevenue = memberships
      .filter(m => m.plan === 'monthly' && m.status === 'active')
      .length * 9.99 / 30; // Daily revenue estimate

    return {
      activeMembers,
      todaysExpressOrders,
      todaysRevenue,
      totalMembers: memberships.length,
      lastUpdated: now.toISOString(),
    };
  } catch (error) {
    console.error('Error calculating dashboard metrics:', error);
    throw new Error('Failed to calculate dashboard metrics');
  }
};

/**
 * Set up real-time listeners for analytics data
 */
export const createAnalyticsListener = (
  callback: (snapshot: QuerySnapshot<DocumentData>) => void
) => {
  const membershipsRef = collection(db, 'rush_d_memberships');
  return onSnapshot(membershipsRef, callback);
};

/**
 * Export analytics data
 */
export const exportAnalyticsData = async (
  startDate: Date,
  endDate: Date,
  format: 'csv' | 'json' = 'json'
) => {
  try {
    const [membershipMetrics, usageMetrics, financialMetrics] = await Promise.all([
      calculateMembershipMetrics(startDate, endDate),
      calculateUsageMetrics(startDate, endDate),
      calculateFinancialMetrics(startDate, endDate),
    ]);

    const analyticsData = {
      dateRange: { 
        startDate: startDate.toISOString(), 
        endDate: endDate.toISOString() 
      },
      membership: membershipMetrics,
      usage: usageMetrics,
      financial: financialMetrics,
      generatedAt: new Date().toISOString(),
    };

    if (format === 'json') {
      return {
        data: JSON.stringify(analyticsData, null, 2),
        filename: `rushd-analytics-${startDate.toISOString().split('T')[0]}-to-${endDate.toISOString().split('T')[0]}.json`,
        contentType: 'application/json'
      };
    } else {
      // Convert to CSV format
      const csvLines: string[] = ['Metric,Value,Type'];
      
      // Add membership metrics
      Object.entries(membershipMetrics).forEach(([key, value]) => {
        csvLines.push(`${key},${value},membership`);
      });
      
      // Add usage metrics
      Object.entries(usageMetrics).forEach(([key, value]) => {
        if (typeof value === 'object' && Array.isArray(value)) {
          csvLines.push(`${key},"${JSON.stringify(value)}",usage`);
        } else {
          csvLines.push(`${key},${value},usage`);
        }
      });
      
      // Add financial metrics
      Object.entries(financialMetrics).forEach(([key, value]) => {
        csvLines.push(`${key},${value},financial`);
      });

      return {
        data: csvLines.join('\n'),
        filename: `rushd-analytics-${startDate.toISOString().split('T')[0]}-to-${endDate.toISOString().split('T')[0]}.csv`,
        contentType: 'text/csv'
      };
    }
  } catch (error) {
    console.error('Error exporting analytics data:', error);
    throw new Error('Failed to export analytics data');
  }
};

/**
 * Clear analytics cache
 */
export const clearAnalyticsCache = (): void => {
  analyticsCache.clear();
};

/**
 * Preload analytics data for better performance
 */
export const preloadAnalyticsData = async (): Promise<void> => {
  try {
    await Promise.all([
      fetchMemberships(false), // Force fresh fetch
      fetchRefunds(false)
    ]);
  } catch (error) {
    console.error('Error preloading analytics data:', error);
  }
};

/**
 * Debug function to manually check recent orders
 */
export const debugRecentOrders = fetchRecentOrdersForDebugging;
